import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addNewEntityDataToEntityListData,
  getEntityDataById,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { requireAuthenticatedUser } from '../../decorators'
import {
  findCaseEmailSuppression,
  setCaseEmailReadStatus,
  suppressedRecipientsBody,
} from '../caseEmails/mockData'
import { readStatusChangeRefusal } from '../../modules/emailReadStatusRules'
import { setServiceRequestEmailReadStatus } from '../serviceRequestEmails/mockData'
import {
  isMockMessage,
  messageChangeRefusal,
  nothingToAttachFrom,
  queuedMessageFields,
  sendingRecord,
  sendRefusal,
  statusRefusal,
  unknownDocument,
  type IRefusal,
} from '../../modules/activityEmailRules'
import { getEmailListData } from './mockData'
import { getOrderDocumentListDataByOrderId } from '../orderDocuments/mockData'
import { getOpportunityDocumentListDataByOpportunityId } from '../opportunityDocuments/mockData'
import { getCompanyDocumentListDataByCompanyId } from '../companyDocuments/mockData'
import { getSupplierDocumentListDataBySupplierId } from '../supplierDocuments/mockData'
import { getTsdDocumentListDataByTsdId } from '../tsdDocuments/mockData'
import type { TEntityDocument } from '~/types/document/models'
import { EmailStatusIds } from '~/services/selectLists/emailStatii'
import type { THrid, TEntityDataForPost } from '~/types/models'
import type { IActivityEmail, IEmailAttachment } from '~/types/email/models'

interface GetEmailsResponse {
  data: IActivityEmail[]
}

async function getEmails(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  console.log(`'Returning emails.`)
  const responseBody = { data: await getEmailListData() }
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getEmail_byId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const email = (await getEmailListData()).find(
    (e: IActivityEmail) => e.id === req.params.id
  )
  return email
    ? res(ctx.status(200), ctx.json({ data: email }))
    : res(ctx.status(404), ctx.json({ status: 404, title: 'Not Found' }))
}

// POST and PATCH follow cynque-api-server's hand-written routes (#1770): see
// api/msw/modules/activityEmailRules.ts for the rules and why the mock keeps
// them.

// Beside the email's own fields; never stored.
const REQUEST_ONLY_KEYS = ['documentIds', 'sendToSuppressed', 'files']

function refuse(res: ResponseFunction, ctx: RestContext, refusal: IRefusal) {
  return res(ctx.status(refusal.status), ctx.json(refusal.body))
}

/** The email, as JSON or as a multipart `payload` part, and its new files. */
async function readEmailRequest(
  req: RestRequest
): Promise<{ email: Record<string, any>; files: File[] }> {
  const contentType = req.headers.get('content-type') || ''
  if (!contentType.includes('multipart/form-data')) {
    return { email: await req.json(), files: [] }
  }
  const body = req.body as Record<string, string | File | File[]>
  const files = ([] as unknown[])
    .concat(body.files ?? [])
    .filter((f): f is File => f instanceof File)
  return { email: JSON.parse(String(body.payload ?? '{}')), files }
}

// Each new file becomes a document of the sending record. The mock records the
// attachment, not a separate document row.
function newFileAttachments(email: Record<string, any>, files: File[]) {
  return files.map(
    (file, index): IEmailAttachment => ({
      documentId: `${email.id}-file-${index + 1}`,
      filename: file.name,
      contentType: file.type,
      byteSize: file.size,
      fileUrlPath: `/mock-files/${email.id}/${file.name}?sig=mock`,
    })
  )
}

// The documents of each kind of sending record, as the server finds them.
const DOCUMENTS_OF: Record<string, (id: string) => Promise<TEntityDocument[]>> =
  {
    order: getOrderDocumentListDataByOrderId,
    opportunity: getOpportunityDocumentListDataByOpportunityId,
    company: getCompanyDocumentListDataByCompanyId,
    supplier: getSupplierDocumentListDataBySupplierId,
    tsd: getTsdDocumentListDataByTsdId,
  }

// `documentIds` as the server attaches them: each must be a document of the
// sending record, or the whole request is refused.
async function documentAttachments(
  email: Record<string, any>
): Promise<{ attachments: IEmailAttachment[] } | { refusal: IRefusal }> {
  const requested: string[] = email.documentIds ?? []
  const record = sendingRecord(email)
  const documentsOf = record && DOCUMENTS_OF[record.kind]
  if (!requested.length || !record || !documentsOf) {
    return { attachments: [] }
  }
  const documents = (await documentsOf(record.id)).filter((d) =>
    requested.includes(d.id)
  )
  const asked = new Set(requested).size
  if (documents.length !== asked) {
    return { refusal: unknownDocument(record.kind, documents.length, asked) }
  }
  return {
    attachments: documents.map((d) => ({
      documentId: d.id,
      filename: d.filename,
      contentType: d.filetype,
      byteSize: d.filesize,
      fileUrlPath: `/mock-files/${d.id}/${d.filename}?sig=mock`,
    })),
  }
}

function suppressionRefusal(email: Partial<IActivityEmail>) {
  const suppressions = [
    ...(email.toAddresses ?? []),
    ...(email.ccAddresses ?? []),
    ...(email.bccAddresses ?? []),
  ]
    .map((address) => findCaseEmailSuppression(address))
    .filter((found): found is NonNullable<typeof found> => !!found)
  return suppressions.length
    ? { status: 409 as const, body: suppressedRecipientsBody(suppressions) }
    : undefined
}

function withoutRequestOnlyKeys(email: Record<string, any>) {
  const stored = { ...email }
  REQUEST_ONLY_KEYS.forEach((key) => delete stored[key])
  return stored
}

const postEmail = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { email, files } = await readEmailRequest(req)
  const statusId = email.status?.id
  const statusProblem = statusRefusal({
    statusId,
    sentAt: email.sentAt,
    isMessage: false,
  })
  if (statusProblem) {
    return refuse(res, ctx, statusProblem)
  }
  if ((files.length || email.documentIds?.length) && !sendingRecord(email)) {
    return refuse(res, ctx, nothingToAttachFrom())
  }
  const documents = await documentAttachments(email)
  if ('refusal' in documents) {
    return refuse(res, ctx, documents.refusal)
  }
  const isSend = statusId === EmailStatusIds.READY_TO_SEND
  if (isSend) {
    const refusal =
      sendRefusal(email) ??
      (email.sendToSuppressed ? undefined : suppressionRefusal(email))
    if (refusal) {
      return refuse(res, ctx, refusal)
    }
  }
  const toStore = {
    ...withoutRequestOnlyKeys(email),
    attachments: [
      ...documents.attachments,
      ...newFileAttachments(email, files),
    ],
    ...(isSend ? queuedMessageFields(email.id) : { direction: null }),
  }
  const data = await addNewEntityDataToEntityListData({
    req,
    newEntityData: toStore as TEntityDataForPost,
    getEntityListDataFn: getEmailListData,
  })
  return res(ctx.status(201), ctx.json({ data }))
}

const patchEmail = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const id = String(req.params.id)
  const changes: Partial<IActivityEmail> = await req.json()
  const stored = (await getEntityDataById(
    id,
    getEmailListData
  )) as IActivityEmail
  const messageRefusal = messageChangeRefusal({ id, stored, changes })
  if (messageRefusal) {
    return refuse(res, ctx, messageRefusal)
  }
  const statusId = (changes.status?.id ?? stored.status?.id) as number
  const isMessage = isMockMessage(stored)
  if (!isMessage) {
    const statusProblem = statusRefusal({
      statusId,
      sentAt: 'sentAt' in changes ? changes.sentAt : stored.sentAt,
      isMessage,
    })
    if (statusProblem) {
      return refuse(res, ctx, statusProblem)
    }
  }
  const isSend =
    !isMessage &&
    'status' in changes &&
    statusId === EmailStatusIds.READY_TO_SEND
  if (isSend) {
    const merged = { ...stored, ...changes }
    const refusal =
      sendRefusal(merged) ??
      (changes.sendToSuppressed ? undefined : suppressionRefusal(merged))
    if (refusal) {
      return refuse(res, ctx, refusal)
    }
  }
  return updateEntityListData(req, res, ctx, getEmailListData, [
    ({ targetItem }: { targetItem: Record<string, any> }) => {
      REQUEST_ONLY_KEYS.forEach((key) => delete targetItem[key])
      if (isSend) {
        Object.assign(targetItem, queuedMessageFields(id))
      }
    },
  ])
}

const deleteEmail = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getEmailListData)
}

/**
 * PATCH /api/emails/:id/readStatus -- Read / Unread by hand (cynque-api-server
 * #1814, ahead of the server: see emailReadStatusRules.ts). No `version`, and
 * the email's own version is left alone.
 *
 * One table holds every email server-side; the mock keeps case, service
 * request and activity emails apart, so it looks in all three. No activity email in the mock is
 * inbound, so one found there is always refused -- which is the point: it must
 * be a 400 for "not inbound", not a 404.
 */
const patchEmailReadStatus = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const id = String(req.params.id)
  const refuse = (detail: string) =>
    res(
      ctx.status(400),
      ctx.json({ status: 400, title: 'Validation failed', detail })
    )
  const body = (await req.json()) as { id?: unknown }
  const unknownKeys = Object.keys(body ?? {}).filter((key) => key !== 'id')
  if (!body || unknownKeys.length) {
    return refuse(`Expected { "id": <read status> }.`)
  }
  const caseResult = await setCaseEmailReadStatus(id, body.id)
  if (caseResult) {
    return 'refusal' in caseResult
      ? refuse(caseResult.refusal)
      : res(ctx.status(200), ctx.json({ data: caseResult.caseEmail }))
  }
  // Service request emails: their own store in the mock (Service Requests are
  // webapp/mock-only until their server work), the same rules.
  const serviceRequestResult = await setServiceRequestEmailReadStatus(
    id,
    body.id
  )
  if (serviceRequestResult) {
    return 'refusal' in serviceRequestResult
      ? refuse(serviceRequestResult.refusal)
      : res(
          ctx.status(200),
          ctx.json({ data: serviceRequestResult.serviceRequestEmail })
        )
  }
  const activityEmail = (await getEmailListData()).find((e) => e.id === id)
  if (!activityEmail) {
    return res(ctx.status(404), ctx.json({ status: 404, title: 'Not Found' }))
  }
  return refuse(
    readStatusChangeRefusal(activityEmail, body.id) ??
      'Only an inbound email has a read status.'
  )
}

export const handlers = [
  {
    method: 'get',
    path: '/api/emails',
    handler: getEmails,
  },
  {
    method: 'get',
    path: '/api/emails/:id',
    handler: getEmail_byId,
  },
  {
    method: 'post',
    path: '/api/emails',
    handler: postEmail,
  },
  {
    method: 'patch',
    path: '/api/emails/:id/readStatus',
    handler: patchEmailReadStatus,
  },
  {
    method: 'patch',
    path: '/api/emails/:id',
    handler: patchEmail,
  },
  {
    method: 'delete',
    path: '/api/emails/:id',
    handler: deleteEmail,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
